import { Camera, ImageOff, Loader2, Trash2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeleteVesselPhoto,
  useUploadVesselPhoto,
  useVesselPhotoUrl,
} from '@/features/vessel-storage/queries';
import {
  ALLOWED_PHOTO_TYPES,
  PHOTO_MAX_BYTES,
} from '@/features/vessel-storage/types';
import { ApiError } from '@/lib/api';

interface VesselPhotoCardProps {
  vesselId: string;
  /** From the parent — we read it to decide between empty/preview
   *  state without an extra request before the URL has loaded. */
  photoKey: string | null;
}

export function VesselPhotoCard({ vesselId, photoKey }: VesselPhotoCardProps) {
  const { t } = useTranslation();
  const { data: photoUrl, isLoading: isUrlLoading } = useVesselPhotoUrl(
    photoKey ? vesselId : undefined,
  );
  const uploadMut = useUploadVesselPhoto(vesselId);
  const deleteMut = useDeleteVesselPhoto(vesselId);

  const [progress, setProgress] = useState<number | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorParams, setErrorParams] = useState<Record<string, unknown>>();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  // Clear local error when a new mutation kicks off so old error
  // messages don't linger across attempts.
  useEffect(() => {
    if (uploadMut.isPending || deleteMut.isPending) {
      setErrorKey(null);
      setErrorParams(undefined);
    }
  }, [uploadMut.isPending, deleteMut.isPending]);

  const onFiles = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setProgress(0);
    uploadMut.mutate(
      {
        file,
        onProgress: (loaded, total) =>
          setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0),
      },
      {
        onSettled: () => setProgress(null),
        onError: (err) => {
          if (err instanceof ApiError) {
            setErrorKey(err.key);
            setErrorParams(err.params);
          } else {
            setErrorKey('vessel.photo.uploadError');
          }
        },
      },
    );
  };

  const onRemove = () => {
    deleteMut.mutate(undefined, {
      onSuccess: () => setConfirmRemoveOpen(false),
      onError: (err) => {
        setConfirmRemoveOpen(false);
        if (err instanceof ApiError) {
          setErrorKey(err.key);
          setErrorParams(err.params);
        } else {
          setErrorKey('vessel.photo.removeError');
        }
      },
    });
  };

  const isBusy = uploadMut.isPending || deleteMut.isPending;
  const hasPhoto = Boolean(photoKey);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera aria-hidden className="h-4 w-4" />
            {t('vessel.photo.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorKey && (
            <Alert variant="destructive">
              <AlertTitle>{t('vessel.photo.uploadError')}</AlertTitle>
              <AlertDescription>
                {t(errorKey, {
                  defaultValue: t('vessel.photo.uploadError'),
                  ...(errorParams ?? {}),
                })}
              </AlertDescription>
            </Alert>
          )}

          {hasPhoto ? (
            <div className="space-y-3">
              {isUrlLoading || !photoUrl ? (
                <Skeleton className="aspect-video w-full" />
              ) : (
                <img
                  src={photoUrl.url}
                  alt={t('vessel.photo.title')}
                  className="aspect-video w-full rounded-md object-cover"
                />
              )}
              <div className="flex flex-wrap gap-2">
                <FileDropzone
                  accept={ALLOWED_PHOTO_TYPES.join(',')}
                  maxSizeBytes={PHOTO_MAX_BYTES}
                  maxFiles={1}
                  disabled={isBusy}
                  label={
                    progress !== null
                      ? t('vessel.photo.uploading')
                      : t('vessel.photo.replace')
                  }
                  onFilesSelected={onFiles}
                  onError={(e) => {
                    setErrorKey(e.key);
                    setErrorParams(e.params);
                  }}
                  className="flex-1 py-3"
                />
                <Button
                  variant="destructive"
                  onClick={() => setConfirmRemoveOpen(true)}
                  disabled={isBusy}
                >
                  {deleteMut.isPending ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : (
                    <Trash2 aria-hidden />
                  )}
                  {t('vessel.photo.remove')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed bg-muted/30 text-muted-foreground">
                <div className="flex flex-col items-center gap-1 text-sm">
                  <ImageOff className="h-6 w-6" aria-hidden />
                  <span>{t('vessel.photo.empty')}</span>
                </div>
              </div>
              <FileDropzone
                accept={ALLOWED_PHOTO_TYPES.join(',')}
                maxSizeBytes={PHOTO_MAX_BYTES}
                maxFiles={1}
                disabled={isBusy}
                label={
                  progress !== null ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {t('vessel.photo.uploading')}
                      {progress > 0 ? ` ${progress}%` : null}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Upload className="h-4 w-4" aria-hidden />
                      {t('vessel.photo.upload')}
                    </span>
                  )
                }
                onFilesSelected={onFiles}
                onError={(e) => {
                  setErrorKey(e.key);
                  setErrorParams(e.params);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title={t('vessel.photo.removeConfirmTitle')}
        description={t('vessel.photo.removeConfirmDescription')}
        confirmLabel={t('vessel.photo.removeConfirm')}
        variant="destructive"
        loading={deleteMut.isPending}
        onConfirm={onRemove}
      />
    </>
  );
}
